package com.eladfinish.bible.contest.screens.settings.ui.screens

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.LocalAppActions
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.analytics.AnalyticsConstants
import com.eladfinish.bible.contest.analytics.InstallationIdUserProperty
import com.eladfinish.bible.contest.common.settings.KeyValueSettings
import com.eladfinish.bible.contest.constants.BooleanConstants.ENABLE_ANONYMOUS_USAGE_STATS
import com.eladfinish.bible.contest.constants.BooleanConstants.ENABLE_HIDE_GOD_NAME
import com.eladfinish.bible.contest.constants.Constants.PREF_APP_FONT_SIZE
import com.eladfinish.bible.contest.constants.Constants.PREF_ENABLE_ANONYMOUS_USAGE_STATS
import com.eladfinish.bible.contest.constants.Constants.PREF_ENABLE_SHAKE_SWITCH
import com.eladfinish.bible.contest.constants.Constants.PREF_HEBREW_GENDER_LANGUAGE
import com.eladfinish.bible.contest.constants.Constants.PREF_NIKUD_SWITCH
import com.eladfinish.bible.contest.constants.Constants.PREF_SHOW_WITH_TEAMIM
import com.eladfinish.bible.contest.constants.Constants.PREF_SOUND_SWITCH
import com.eladfinish.bible.contest.constants.Constants.PREF_VIBRATION_SWITCH
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.HIDE_GOD_NAME
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.ONLY_QRI_SWITCH
import com.eladfinish.bible.contest.di.config.DebugInfo
import com.eladfinish.bible.contest.di.getKoinInstance
import com.eladfinish.bible.contest.last_updated_format
import com.eladfinish.bible.contest.nikud
import com.eladfinish.bible.contest.platform.BuildDate
import com.eladfinish.bible.contest.pref_description_anonymous_usage_stats
import com.eladfinish.bible.contest.pref_description_nikud
import com.eladfinish.bible.contest.pref_description_nikud_disabled
import com.eladfinish.bible.contest.pref_description_qri
import com.eladfinish.bible.contest.pref_description_qri_disabled
import com.eladfinish.bible.contest.pref_description_shake
import com.eladfinish.bible.contest.pref_description_sounds
import com.eladfinish.bible.contest.pref_description_sounds_disabled
import com.eladfinish.bible.contest.pref_description_teamim
import com.eladfinish.bible.contest.pref_description_teamim_disabled
import com.eladfinish.bible.contest.pref_description_vibrations
import com.eladfinish.bible.contest.pref_description_vibrations_disabled
import com.eladfinish.bible.contest.pref_font_size_title
import com.eladfinish.bible.contest.pref_font_type_title
import com.eladfinish.bible.contest.pref_hebrew_gender_language_title
import com.eladfinish.bible.contest.qri
import com.eladfinish.bible.contest.restore_default
import com.eladfinish.bible.contest.screens.settings.SettingsAction
import com.eladfinish.bible.contest.screens.settings.SettingsEffect
import com.eladfinish.bible.contest.screens.settings.SettingsStore
import com.eladfinish.bible.contest.screens.settings.debug.DEBUG_FLAGS_TITLE
import com.eladfinish.bible.contest.screens.settings.ui.components.model.SubSettingsComponentType
import com.eladfinish.bible.contest.screens.settings.ui.dialogs.SettingsDialogs
import com.eladfinish.bible.contest.screens.settings.ui.preferences.ActionPreference
import com.eladfinish.bible.contest.screens.settings.ui.preferences.DropdownPreference
import com.eladfinish.bible.contest.screens.settings.ui.preferences.LanguagePreference
import com.eladfinish.bible.contest.screens.settings.ui.preferences.PreferenceSwitch
import com.eladfinish.bible.contest.send_anonymous_usage_stats
import com.eladfinish.bible.contest.shake
import com.eladfinish.bible.contest.size_huge
import com.eladfinish.bible.contest.size_large
import com.eladfinish.bible.contest.size_medium
import com.eladfinish.bible.contest.size_small
import com.eladfinish.bible.contest.size_tiny
import com.eladfinish.bible.contest.sounds
import com.eladfinish.bible.contest.teamim
import com.eladfinish.bible.contest.ui.common.supportedFonts
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import com.eladfinish.bible.contest.ui.extensions.navigationBarContentPadding
import com.eladfinish.bible.contest.version_format_one_argument
import com.eladfinish.bible.contest.vibrations
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.compose.stringResource
import dev.icerock.moko.resources.format
import kotlinx.collections.immutable.toImmutableList
import org.koin.compose.viewmodel.koinViewModel

const val FONT_PREFERENCE_KEY = "selected_font"

@Composable
internal fun SettingsScreenRoot(
    store: SettingsStore = koinViewModel(),
    settings: KeyValueSettings,
    onComponentClick: (SubSettingsComponentType) -> Unit,
    versionInfo: String,
    resetPreferences: () -> Unit,
    onVersionInfoClick: (String) -> Unit,
    logEvent: (eventName: String, singleKey: String, singleValue: String) -> Unit,
    onDebugFlagsClick: () -> Unit,
    debugInfo: DebugInfo = getKoinInstance(),
) {
    val state by store.observeState().collectAsState()
    val appActions = LocalAppActions.current

    LaunchedEffect(Unit) {
        store.observeSideEffect().collect {
            when (it) {
                SettingsEffect.ApplyFontChange -> appActions.restartApp()
            }
        }
    }

    SettingsCells(
        onComponentClick = onComponentClick,
        store = store,
        logEvent = logEvent,
        resetPreferences = resetPreferences,
        versionInfo = versionInfo,
        onVersionInfoClick = onVersionInfoClick,
        settings = settings,
        onDebugFlagsClick = onDebugFlagsClick.takeIf { debugInfo.isDebugMode },
    )

    SettingsDialogs(
        dialogState = state.dialogState,
        onAction = store::dispatch,
    )
}

@Composable
private fun SettingsCells(
    onComponentClick: (SubSettingsComponentType) -> Unit,
    settings: KeyValueSettings,
    store: SettingsStore,
    logEvent: (String, String, String) -> Unit,
    resetPreferences: () -> Unit,
    versionInfo: String,
    onVersionInfoClick: (String) -> Unit,
    onDebugFlagsClick: (() -> Unit)?,
) {
    val appActions = LocalAppActions.current
    // Rows only right-align their RTL text within whatever width they're given (see
    // ActionPreference/PreferenceSwitch's Modifier.fillMaxWidth()) — on a phone that width is the
    // screen, which is already a comfortable reading measure, but on a wide desktop window it
    // stretched edge-to-edge and left a huge empty gap to the left of every row. Capping the list
    // itself at a readable width and centering it fixes that without touching each row.
    val listState = rememberLazyListState()

    Box(modifier = Modifier.fillMaxSize()) {
        LazyColumn(
            state = listState,
            // widthIn() must come before fillMaxWidth() here: a modifier chain applies
            // outside-in during measurement, so fillMaxWidth() first would lock min=max=full
            // window width right away, leaving the later widthIn(max=...) nothing to shrink —
            // this order caps the incoming constraint first, then fills exactly up to that cap.
            modifier = Modifier
                .align(Alignment.TopCenter)
                .fillMaxHeight()
                .widthIn(max = 700.dp)
                .fillMaxWidth()
                .mouseVerticalScrollbar(listState),
            contentPadding = navigationBarContentPadding(horizontal = 16.dp, top = 16.dp, bottom = 16.dp),
        ) {
        if (onDebugFlagsClick != null) {
            item {
                ActionPreference(
                    title = DEBUG_FLAGS_TITLE,
                    summary = "הדלקה, כיבוי ואיפוס של הדגלים הבוליאניים השמורים",
                    onClick = onDebugFlagsClick,
                )
                Divider()
            }
        }

        SubSettingsComponentType.entries.forEach { type ->
            item {
                ActionPreference(
                    title = type.title.localized(),
                    summary = type.subtitle.localized(),
                    onClick = {
                        onComponentClick(type)
                    }
                )
                Divider()
            }
        }

        item {
            PreferenceSwitch(
                title = stringResource(SharedRes.strings.sounds),
                key = PREF_SOUND_SWITCH,
                defaultValue = true,
                summaryOn = stringResource(SharedRes.strings.pref_description_sounds),
                summaryOff = stringResource(SharedRes.strings.pref_description_sounds_disabled),
                settings = settings,
            )
            Divider()

            PreferenceSwitch(
                title = stringResource(SharedRes.strings.vibrations),
                key = PREF_VIBRATION_SWITCH,
                defaultValue = true,
                summaryOn = stringResource(SharedRes.strings.pref_description_vibrations),
                summaryOff = stringResource(SharedRes.strings.pref_description_vibrations_disabled),
                settings = settings,
            )
            Divider()

            PreferenceSwitch(
                title = stringResource(SharedRes.strings.shake),
                key = PREF_ENABLE_SHAKE_SWITCH,
                defaultValue = true,
                summaryOn = stringResource(SharedRes.strings.pref_description_shake),
                summaryOff = stringResource(SharedRes.strings.pref_description_shake),
                settings = settings,
            )
            Divider()

            PreferenceSwitch(
                title = stringResource(SharedRes.strings.nikud),
                key = PREF_NIKUD_SWITCH,
                defaultValue = true,
                summaryOn = stringResource(SharedRes.strings.pref_description_nikud),
                summaryOff = stringResource(SharedRes.strings.pref_description_nikud_disabled),
                settings = settings,
            )
            Divider()

            PreferenceSwitch(
                title = stringResource(SharedRes.strings.teamim),
                key = PREF_SHOW_WITH_TEAMIM,
                defaultValue = true,
                summaryOn = stringResource(SharedRes.strings.pref_description_teamim),
                summaryOff = stringResource(SharedRes.strings.pref_description_teamim_disabled),
                settings = settings,
            )
            Divider()

            PreferenceSwitch(
                title = stringResource(SharedRes.strings.qri),
                key = ONLY_QRI_SWITCH,
                defaultValue = true,
                summaryOn = stringResource(SharedRes.strings.pref_description_qri),
                summaryOff = stringResource(SharedRes.strings.pref_description_qri_disabled),
                settings = settings,
            )
            Divider()

            PreferenceSwitch(
//                title = stringResource(SharedRes.strings.hide_god_name),
                title = "צנזור שם ה'",
                key = HIDE_GOD_NAME,
                defaultValue = ENABLE_HIDE_GOD_NAME,
                summaryOn = "שם ה' יופיע בהפרדה (י‑הוה)",
                summaryOff = "שם ה' יופיע ככתבו",
                settings = settings,
            )
            Divider()

            PreferenceSwitch(
                title = stringResource(SharedRes.strings.send_anonymous_usage_stats),
                key = PREF_ENABLE_ANONYMOUS_USAGE_STATS,
                defaultValue = ENABLE_ANONYMOUS_USAGE_STATS,
                summaryOn = stringResource(SharedRes.strings.pref_description_anonymous_usage_stats),
                summaryOff = stringResource(SharedRes.strings.pref_description_anonymous_usage_stats),
                settings = settings,
                // The installation_id user property follows the consent at once, not at the next launch.
                onCheckedChange = { getKoinInstance<InstallationIdUserProperty>()() },
            )
            Divider()

            DropdownPreference(
                title = stringResource(SharedRes.strings.pref_font_type_title),
                key = FONT_PREFERENCE_KEY,
                entries = supportedFonts.map { it.removeSuffix(".ttf") }.toImmutableList(),
                entryValues = supportedFonts.toImmutableList(),
                onClick = {
                    store.dispatch(SettingsAction.FontChanged(selectedFont = it))
                },
                settings = settings,
            )
            Divider()

            val showPrefFontSize = false
            if (showPrefFontSize) {
                DropdownPreference(
                    title = stringResource(SharedRes.strings.pref_font_size_title),
                    key = PREF_APP_FONT_SIZE,
                    enabled = false,
                    entries = listOf(
                        SharedRes.strings.size_tiny,
                        SharedRes.strings.size_small,
                        SharedRes.strings.size_medium,
                        SharedRes.strings.size_large,
                        SharedRes.strings.size_huge,
                    ).map { stringResource(it) }.toImmutableList(),
                    entryValues = listOf(-7, -4, -2, 1, 4).map { it.toString() }.toImmutableList(),
                    settings = settings,
                )

                Divider()
            }

            DropdownPreference(
                title = stringResource(SharedRes.strings.pref_hebrew_gender_language_title),
                key = PREF_HEBREW_GENDER_LANGUAGE,
                // TODO: i18n extract to string resources
                entries = listOf(
                    "לשון זכר",
                    "לשון נקבה",
                ).toImmutableList(),
                entryValues = listOf(
                    "male",
                    "female",
                ).toImmutableList(),
                onClick = {
                    appActions.restartApp()
                    logEvent(
                        AnalyticsConstants.SetHebrewGender.EVENT_SETTINGS,
                        AnalyticsConstants.SetHebrewGender.GENDER,
                        it,
                    )
                },
                settings = settings,
            )

            Divider()

            LanguagePreference(
                settings = settings,
            )

            Divider()

            ActionPreference(
                title = stringResource(SharedRes.strings.restore_default),
                // TODO: i18n extract to string resources
                summary = "איפוס הגדרות והעדפות",
                onClick = {
                    resetPreferences()
                }
            )

            Divider()

            ActionPreference(
                title = SharedRes.strings.version_format_one_argument.format(versionInfo).localized(),
                summary = SharedRes.strings.last_updated_format.format(BuildDate.display()).localized(),
                onClick = {
                    onVersionInfoClick(versionInfo)
                }
            )
        }
        }
    }
}

@Composable
internal fun Divider() {
    HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
}
